import { Alert, Stack, Typography } from '@mui/material';
import { ConditionsNew } from '@retail/calculus/types';
import { SalesPriceConditionValue } from '@retail/calculus/components';
import { useAppTFunction } from '@retail/app/i18n';
import { ConditionStepType } from '../allConditionsSchema';

interface Props {
  area: ConditionStepType;
  condition: ConditionsNew;
}

/**
 * Shows, inside an edit step, what value is currently inherited for that area — but only
 * when the row has no own condition for it. The form field itself stays blank so the user
 * can fill one in to override the inherited value at this level.
 */
export function InheritedConditionHint({ area, condition }: Props) {
  const t = useAppTFunction();

  const subCondition = {
    purchase: condition.purchaseCondition,
    freight: condition.freightCondition,
    salesPrice: condition.priceSourceCondition,
    rounding: condition.roundingCondition,
  }[area];

  if (!subCondition?.isInherited) return null;

  const renderValue = () => {
    switch (area) {
      case 'purchase':
        return (
          <Typography variant="body2" fontWeight="bold">
            {condition.purchaseCondition?.agreementText ??
              condition.purchaseCondition?.agreementNumber}
          </Typography>
        );
      case 'freight': {
        const freight = condition.freightCondition;
        const value = freight?.percentAddon
          ? `${freight.percentAddon}%`
          : freight?.fixedAddOn
          ? t('freight.columns.pricePerUnit', {
              price: freight.fixedAddOn,
              unit: freight.fixedAddOnUnit,
            })
          : '';
        return (
          <Typography variant="body2" fontWeight="bold">
            {value}
          </Typography>
        );
      }
      case 'salesPrice':
        return <SalesPriceConditionValue condition={condition} />;
      case 'rounding':
        return (
          <Typography variant="body2" fontWeight="bold">
            {condition.roundingCondition?.ruleSetName}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Stack gap={0.5}>
        <Typography variant="body2">
          {t('calculus.createAllConditions.inheritedFrom', {
            level: subCondition.inheritedBinding?.bindingTargetName ?? '',
          })}
        </Typography>
        {renderValue()}
      </Stack>
    </Alert>
  );
}
